/**
 * Nav data types — shared by every file under `nav/parts/` and by `items.ts`,
 * the assembler that concatenates them.
 *
 * WP0 (nav sharding, docs §19161) split `nav/items.ts` — one 3,685-line file
 * holding 32 flat groups — into one file per FSD part. This file holds what
 * moved to the top: the types those part files build, and the pure label/
 * keyword resolvers that only need a `NavItem`/`NavGroup` to do their job.
 * `NAV_GROUPS`, `visibleGroups`, `visibleItems` and `itemForPath` stayed in
 * `items.ts` because they operate on the *assembled* nav, not on one part.
 */

import { t } from '@/i18n'
import type { AppIconName } from '@/lib/icons'

export interface NavItem {
  /** Stable id — also the localStorage key for favourites and recents. */
  id: string
  /**
   * Message key for the visible label. The declaration holds keys rather than
   * words because it is module-level data evaluated once at import: a literal
   * here would be frozen in whichever locale happened to be active at boot.
   */
  labelKey: string
  /** Router path. */
  to: string
  icon: AppIconName
  /** Required permission. Items without one are available to any signed-in user. */
  permission?: string
  /** Key for extra palette search terms, resolved to a space-separated list. */
  keywordsKey?: string
  /** Key for a one-line explanation, shown in the palette. */
  descriptionKey?: string
  /**
   * FSD section number this item implements, when it differs from (or
   * refines) its section's own `fsd` — e.g. `'10.2.8'` for an item that sits
   * in an Inventory section but implements a POS feature, or a sub-section
   * like `'D.9.1.8'`. Omit it to inherit the section's number.
   *
   * The rule for both FSD fields, on items and sections alike:
   * - `fsd` is ONLY an FSD section number: `'1.2.7'`, `'2.1'`, `'D.9.2'`,
   *   `'17'`, or a sub-section such as `'D.9.1.8'`.
   * - `code` is an FSD identifier: a feature code (`'FND-F07'`) or a
   *   requirement id (`'FND-FR-084'`, `'XFN-EX-005'`, `'AI-FR-031'`).
   *   Requirement ids never go in `fsd`.
   *
   * Resolution against the section is `resolveFsd` / `navFsdOf` in
   * `nav/apps.ts`.
   */
  fsd?: string
  /** FSD feature code or requirement id for this item. See `fsd` for the rule. */
  code?: string
  /**
   * Further FSD section numbers (same format as `fsd`) that this screen ALSO
   * implements — a multi-spec screen such as POS settings, which configures
   * 10.2.6–10.2.8 as well as its own 10.2.1. Rendered only as extra chips in
   * the page header (`components/PageHeader.vue`). Never used for grouping,
   * section placement, filter grouping or search: the item's home is still
   * its `fsd` / its section.
   */
  related?: readonly string[]
  /**
   * C17: marks ONLY a recorded deviation — a screen with no FSD
   * specification that is kept because removing it would break the product
   * (users, roles, profile; DECISIONS.md C17). Every other item's FSD
   * reference now lives in the nav data itself (`fsd` / `code` here and on
   * its section), and is rendered as chips in the screen's page header — no
   * longer in description copy. Find every flagged item with
   * `grep -rn "fsdStatus: 'extraneous'" web/src/nav/parts/`. `isExtraneous`
   * (`nav/apps.ts`) drives the "Not in FSD" badge (`components/FsdBadge.vue`)
   * in the sidebar, launcher, palette, home screen and page header. It stays
   * an annotation, never a behavioural flag — do not gate visibility or
   * permissions on it.
   */
  fsdStatus?: 'extraneous'
}

/**
 * What a section is, in FSD terms:
 * - `overview` — a part's landing section (`<partId>-overview`), no FSD number.
 * - `feature` — a feature specification, `N.2.y`.
 * - `catalogue` — a part's catalogue `N.1`, or Part 17 (AI).
 * - `crosscut` — a Part D cross-cutting specification, `D.x`.
 */
export type NavSectionKind = 'overview' | 'feature' | 'catalogue' | 'crosscut'

/**
 * A section groups related items under one heading — what this file used to
 * call, and what every consumer still calls, a "group". Kept under its
 * original name (rather than renamed to e.g. `NavSection`) because
 * `AppSidebar.vue`, `CommandPaletteHost.vue` (via `useCommands.ts`),
 * `useNavPrefs.ts`, `AppBreadcrumbs.vue` and both nav test files all import
 * `NavGroup` by that name, and a FSD "part" (below) is a new level ABOVE this
 * one, not a replacement for it.
 */
export interface NavGroup {
  id: string
  labelKey: string
  items: NavItem[]
  kind?: NavSectionKind
  /** FSD section number this section presents (see `NavItem.fsd` for the rule). */
  fsd?: string
  /** FSD feature code for the section; omitted for catalogue, D.2–D.8, 17. */
  code?: string
  /** Message key for the FSD's full section title; `labelKey` is the short one. */
  fullTitleKey?: string
}

/**
 * One app's worth of navigation: FSD Parts 1–15, the Platform container (FSD
 * Part D cross-cutting specifications and Part 17 AI — FSD Part 16 is
 * requirements only, with no screens), and the two pinned, unnumbered
 * containers ("Home", "Me").
 *
 * A part is a container of sections. Since C17 each part is subdivided into
 * the FSD's own sections: feature specifications (`N.2.y`, kind `feature`),
 * the part catalogue (`N.1`, kind `catalogue`), Part D cross-cutting
 * specifications (`D.x`, kind `crosscut`) and Part 17 (AI). Section ids are
 * `s-<fsd>` (`s-1.2.7`, `s-D.9.2`, `s-17`); a part's unnumbered landing
 * section is `<partId>-overview`.
 *
 * `NAV_PARTS` (in `items.ts`) is the hierarchical export; `NAV_GROUPS` —
 * `NAV_PARTS.flatMap((part) => part.sections)` — is the flat view the
 * palette, breadcrumbs and favourites read. `labelKey` values here are
 * `nav.parts.*`; `keys.test.ts` walks `NAV_PARTS` and checks every part,
 * section and item key resolves.
 */
export interface NavPart {
  id: string
  labelKey: string
  sections: NavGroup[]
}

/** The label as the user reads it, in the locale in force right now. */
export function navLabel(item: NavItem): string {
  return t(item.labelKey)
}

export function navGroupLabel(group: NavGroup): string {
  return t(group.labelKey)
}

export function navPartLabel(part: NavPart): string {
  return t(part.labelKey)
}

export function navDescription(item: NavItem): string | undefined {
  return item.descriptionKey === undefined ? undefined : t(item.descriptionKey)
}

/** Palette search terms. The catalogue stores them space-separated per locale. */
export function navKeywords(item: NavItem): string[] | undefined {
  if (item.keywordsKey === undefined) return undefined
  return t(item.keywordsKey).split(/\s+/).filter((word) => word !== '')
}
